import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@core-ui/Components/ui/button';
import { Input } from '@core-ui/Components/ui/input';
import { ScrollArea } from '@core-ui/Components/ui/scroll-area';
import { Skeleton } from '@core-ui/Components/ui/skeleton';
import { Trash2, Search, CirclePlus } from 'lucide-react';
import { type ContactListDto, LoadingState } from '@features/contacts/types/contacts.types';
import { deduplicateContactLists } from '@utils/deduplicateContactsLists.ts';
import { IntegrationsListsFallback } from '@features/contacts/components/IntegrationsListsFallback.tsx';
import { IntegrationsContactTypes } from '@features/Settings/enums/IntegrationsEnums.ts';
import { HubspotLogoTyped, OutreachLogoTyped, SalesforceLogoTyped } from '@assets/index.ts';
import { Badge } from '@core-ui/Components/ui/badge.tsx';
import { Table } from '@core-ui/Components/ui/table.tsx';
import { Card } from '@core-ui/Components/ui/card.tsx';
import SalesforceContacts from './SalesforceContacts';

interface ContactListSidebarProps {
  contactLists: ContactListDto[];
  selectedContactListId: string | null;
  onSelectContactList: (contactListId: string) => void;
  onDeleteContactList: (contactListId: string) => void;
  activeTab: string;
  outreachConnected: boolean;
  hubspotConnected: boolean;
  salesforceConnected: boolean;
  onCreateContactList?: () => void;
  isLoading: boolean;
  contactsLoadingState: LoadingState;
  contactsCount: number;
}

function safeString (value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

export function ContactListItemSkeleton ({ rows = 4 } : { rows: number}): React.JSX.Element {
  return (
    <Card className="flex w-full border border-border rounded-md">
      <div className="grid items-between gap-2 p-1">
        <Table>
          {Array.from({ length: rows }).map((_, i)=> {
            return (
              <div key={`s-${i}`} className="border-b border-border/60 hover:bg-transparent w-full py-2">
                <ul>
                  <li>
                    <Skeleton className="h-8 w-1/2 p-1 rounded-md bg-muted-foreground/20" />
                    <Skeleton className="h-2 w-3/8 mt-2 rounded-md bg-muted-foreground/20" />
                  </li>
                </ul>
              </div>
            );
          })
          }
        </Table>
      </div>
    </Card>
  );
}

export default function ContactListSidebar ({
  contactLists,
  selectedContactListId,
  onSelectContactList,
  onDeleteContactList,
  onCreateContactList,
  isLoading,
  contactsLoadingState,
  activeTab,
  outreachConnected,
  hubspotConnected,
  salesforceConnected,
  contactsCount
}: ContactListSidebarProps): React.JSX.Element {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const sortedContactLists = useMemo(() => {
    const listsArray = [...(contactLists ?? [])];
    listsArray.sort((listA, listB) =>
      safeString(listB.updatedAt).localeCompare(safeString(listA.updatedAt)),
    );
    return listsArray;
  }, [contactLists]);

  const filteredContactLists = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (normalizedQuery === '') {
      return sortedContactLists;
    }


    const filtered = sortedContactLists.filter((contactList) =>
      safeString(contactList.title).toLowerCase().includes(normalizedQuery),
    );

    // Prevents duplicate contact lists on search results
    return deduplicateContactLists(filtered);

  }, [searchQuery, sortedContactLists]);

  const handleSelectContactList = (contactList: ContactListDto): void => {
    if (contactList.id !== '') {
      onSelectContactList(contactList.id);
    }
  };

  const handleDeleteContactList = (
    event: React.MouseEvent,
    contactListId: string,
  ): void => {
    event.stopPropagation();
    onDeleteContactList(contactListId);
  };

  const evaluateIfLinkedAccounts = (): boolean => {
    switch (activeTab.toUpperCase() as IntegrationsContactTypes) {
    case IntegrationsContactTypes.Outreach: return outreachConnected;
    case IntegrationsContactTypes.Hubspot: return hubspotConnected;
    case IntegrationsContactTypes.Salesforce: return salesforceConnected;
    default: return false;
    }
  };

  const getIntegrationName = (): string => {
    const name = Array.from(activeTab);
    
    return String(name[0].toUpperCase())
      .concat(activeTab.substring(1, name.length));
  };

  const selectIcon = (): string => {
    const integration = activeTab.toUpperCase();
    switch (integration as IntegrationsContactTypes) {
    case IntegrationsContactTypes.Outreach: return OutreachLogoTyped;
    case IntegrationsContactTypes.Hubspot: return HubspotLogoTyped;
    case IntegrationsContactTypes.Salesforce: return SalesforceLogoTyped;
    }
  };

  const selectColorsClassByIntegration = (): string => {
    const integration = activeTab.toUpperCase();
    switch (integration as IntegrationsContactTypes) {
    case IntegrationsContactTypes.Outreach: return 'bg-[var(--outreach-badge-color)] border-[var(--outreach-badge-border)] text-[var(--outreach-badge-text)]';
    case IntegrationsContactTypes.Hubspot: return 'bg-[var(--hubspot-badge-color)] border-[var(--hubspot-badge-border)] text-[var(--hubspot-badge-text)]';
    case IntegrationsContactTypes.Salesforce: return 'bg-[var(--salesforce-badge-color)] border-[var(--salesforce-badge-border)] text-[var(--salesforce-badge-text)]';
    default: return 'bg-[var(--csv-badge-color)] border-[var(--csv-badge-border)] text-[var(--csv-badge-text)]';
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="rounded-md border border-border bg-card">
        {/* Header */}
        {activeTab !== 'salesforce' ? (
          <div>
            { activeTab === 'csv' &&
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="text-sm font-semibold">{t('contacts.sidebar.title')}</div>
                <Button
                  data-qa-id="create-list-button"
                  type="button"
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                  onClick={() => {
                    onCreateContactList?.();
                  }}
                >
                  <CirclePlus className="mr-1 h-4 w-4" />
                  {t('contacts.sidebar.create')}
                </Button>
              </div>
            }

            <div className="p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                <Input
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                  }}
                  className="pl-9"
                  placeholder={t('contacts.sidebar.searchPh')}
                />
              </div>
            </div>

            {/* Contact Lists */}
            <ScrollArea className="h-[520px] md:h-[620px]">
              <div className="px-2 pb-2">

                {isLoading ? (
                  <ContactListItemSkeleton rows={8} />
                ) : contactLists.length === 0 ? (
                  activeTab !== 'csv' && !evaluateIfLinkedAccounts() ? (
                    <IntegrationsListsFallback
                      icon={selectIcon()}
                      message={t('contacts.table.noAccounts')}
                      buttonText={t('contacts.table.gotoIntegration', { integration: getIntegrationName() })}
                      integrationName={activeTab}
                    />
                  ) : (
                    <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                      {t('contacts.sidebar.empty')}
                    </div>
                  )

                ) :  (
                  filteredContactLists.map((contactList, index) => {
                    const contactListId = safeString(contactList.id);
                    // This conditional check if the current list is selected
                    // Regular list: matches by id
                    // Folder: matches by title
                    const isSelected = (contactListId !== '' && contactListId === String(selectedContactListId));
                    const contactListTitle = safeString(contactList.title) !== ''
                      ? safeString(contactList.title)
                      : 'Untitled list';

                    return (
                      <div
                        data-qa-id={`contact-list-${index}`}
                        key={index}
                        className={`w-full px-3 py-3 transition border-b border-border/60 ${isSelected ? 'bg-muted/60' : 'hover:bg-muted/40'}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          {/* Contact List Button */}
                          <button
                            type="button"
                            onClick={() => {
                              handleSelectContactList(contactList);
                            }}
                            className="min-w-0 flex-1 text-left"
                            title={contactListTitle}
                          >
                            <div className="flex items-center gap-2 line-clamp-2 break-words text-sm font-medium text-foreground">
                              <span data-qa-id="contact-list-title">{contactListTitle}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={`mt-1 text-xs border-1 rounded-md ${selectColorsClassByIntegration()}`}>
                                {safeString(contactList.type ?? 'CSV')}
                              </Badge>
                              {contactsLoadingState === LoadingState.SUCCESS && contactsCount === 0 && isSelected &&
                                <div>
                                  <Badge className="bg-error/30 border:destructive dark:error-foreground text-error">
                                    {t('contacts.sidebar.noContacts')}
                                  </Badge>
                                </div>
                              }
                            </div>
                          </button>

                          {activeTab === 'csv' &&
                            <button
                              data-qa-id={`delete-list-${index}`}
                              type="button"
                              className="cursor-pointer"
                              title={t('contacts.sidebar.deleteList')}
                              onClick={(event) => {
                                handleDeleteContactList(event, contactListId);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-600 hover:text-red-800" />
                            </button>
                          }
                        </div>
                      </div>
                    );
                  })
                )}

                {/* Footer Create Button */}
                {activeTab === 'csv' &&
                  <div className="px-2 pt-2">
                    <Button
                      type="button"
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                      onClick={() => {
                        onCreateContactList?.();
                      }}
                    >
                      <CirclePlus className="mr-1 h-4 w-4" />
                      {t('contacts.sidebar.create')}
                    </Button>
                  </div>
                }
              </div>
            </ScrollArea>
          </div>
        ) : (
          <SalesforceContacts
            onSelectContactList={onSelectContactList}
            contactLists={contactLists}
            salesforceConnected={salesforceConnected}
          />
        )
        }
      </div>
    </div>
  );
}